import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  searchParams: {
    city: "",
    country: "",
    cityId: "",
    countryId: "",
    checkIn: "",
    checkOut: "",
    rooms: 1,
    adults: 2,
    children: 0,
    childAges: [],
    occupancy: [{ room_no: 1, adult: 2, child: 0, child_age: [] }],
    bookingType: "daily",
    requiredCurrency: "INR",
  },
  results: null, // { session_id, next_token, data: Hotel[] }
  isSearching: false, // true while /hotels/hotel/search is in flight
  filters: {
    minPrice: null,
    maxPrice: null,
    rating: null, // "3" | "4" | "5"
    propertyType: null, // "Hotels" | "Resorts" | "Apartments" | "Villas"
    sorting: null, // one of SORTING_CHOICES
  },
  filterMeta: {
    filterKey: null,
    dbPage: null,
    dbHasMore: false,
  },
  isFiltering: false, // true while /hotels/hotel/filter is in flight
  isFilterActive: false, // true once results came from /hotels/hotel/filter
  selectedHotel: null,
  hotelDetail: null, // { AccessKeys, hotelInfo }
  selectedRoom: null, // { room, option } chosen on detail page
  bookingGuests: null, // { rooms: { "1": { adult, child }, ... } }
  bookingResult: null, // response from hotel booking API (payment secrets, booking id)
  bookingConfirmation: null, // final payment / booking success payload
};

const hotelSearchSlice = createSlice({
  name: "hotelSearch",
  initialState,
  reducers: {
    setHotelSearchParams: (state, action) => {
      Object.assign(state.searchParams, action.payload);
    },
    setHotelResults: (state, action) => {
      state.results = action.payload;
    },
    setHotelSearching: (state, action) => {
      state.isSearching = action.payload;
    },
    appendHotelResults: (state, action) => {
      const incoming = action.payload || {};
      const current = state.results || { session_id: null, next_token: "", data: [] };
      const currentData = Array.isArray(current.data) ? current.data : [];
      const nextData = Array.isArray(incoming.data) ? incoming.data : [];
      state.results = {
        session_id: incoming.session_id ?? current.session_id ?? null,
        next_token: incoming.next_token ?? "",
        data: [...currentData, ...nextData],
      };
    },
    setHotelFilters: (state, action) => {
      Object.assign(state.filters, action.payload);
    },
    resetHotelFilters: (state) => {
      state.filters = initialState.filters;
      state.filterMeta = initialState.filterMeta;
      state.isFilterActive = false;
    },
    setHotelFilterMeta: (state, action) => {
      Object.assign(state.filterMeta, action.payload);
    },
    setHotelFiltering: (state, action) => {
      state.isFiltering = action.payload;
    },
    setHotelFilterActive: (state, action) => {
      state.isFilterActive = action.payload;
    },
    setSelectedHotel: (state, action) => {
      state.selectedHotel = action.payload;
    },
    setHotelDetail: (state, action) => {
      state.hotelDetail = action.payload;
    },
    setSelectedRoom: (state, action) => {
      state.selectedRoom = action.payload;
    },
    setBookingGuests: (state, action) => {
      state.bookingGuests = action.payload;
    },
    setBookingResult: (state, action) => {
      state.bookingResult = action.payload;
    },
    setHotelBookingConfirmation: (state, action) => {
      state.bookingConfirmation = action.payload;
    },
    resetHotelSearch: () => initialState,
  },
});

export const {
  setHotelSearchParams,
  setHotelResults,
  setHotelSearching,
  appendHotelResults,
  setHotelFilters,
  resetHotelFilters,
  setHotelFilterMeta,
  setHotelFiltering,
  setHotelFilterActive,
  setSelectedHotel,
  setHotelDetail,
  setSelectedRoom,
  setBookingGuests,
  setBookingResult,
  setHotelBookingConfirmation,
  resetHotelSearch,
} = hotelSearchSlice.actions;

export default hotelSearchSlice.reducer;
